import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useParams } from 'react-router';

import type { Clause } from '@hotjar/feedback/domain/Surveys/Responses/Filters/Clause';
import { filtersToSearchClauses } from '@hotjar/feedback';
import { useSurveyFilters } from '@hotjar/feedback/hooks/useSurveyFilters';

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 50;

interface SurveyResponsesViewReducerState {
  surveyIdParam: string | undefined;
  filters: Clause[];
  pageSize: number;
  pageNumber: number;
}

interface UseSurveyResponsesViewReducer extends SurveyResponsesViewReducerState {
  setPageSize: (pageSize: number) => void;
  setPageNumber: (pageSize: number) => void;
}

type StateAction =
  | { type: 'setPageSize'; payload: number }
  | { type: 'setPageNumber'; payload: number }
  | { type: 'syncFilters'; payload: Clause[] }
  | { type: 'reset' };

const DEFAULT_STATE: Omit<SurveyResponsesViewReducerState, 'filters' | 'sort'> = {
  surveyIdParam: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  pageNumber: DEFAULT_PAGE_NUMBER,
};

export const useSurveyResponsesViewReducer = (): UseSurveyResponsesViewReducer => {
  const { surveyId: surveyIdParam } = useParams();
  const lastActionRef = useRef<StateAction['type']>();
  const { localFilters, globalFilters, resetFilters } = useSurveyFilters();

  const initState = useCallback(() => {
    return {
      ...DEFAULT_STATE,
      filters: filtersToSearchClauses(localFilters, globalFilters),
    };
  }, [localFilters, globalFilters]);

  const reducer = (
    state: SurveyResponsesViewReducerState,
    action: StateAction,
  ): SurveyResponsesViewReducerState => {
    lastActionRef.current = action.type;
    switch (action.type) {
      case 'setPageSize':
        return { ...state, pageSize: action.payload };
      case 'setPageNumber':
        return { ...state, pageNumber: action.payload };
      case 'syncFilters':
        return {
          ...state,
          filters: action.payload,
        };
      case 'reset':
        return initState();
      default:
        return state;
    }
  };

  const [responsesViewState, dispatch] = useReducer(reducer, DEFAULT_STATE, initState);

  useEffect(() => {
    resetFilters();
    dispatch({ type: 'reset' });
  }, [resetFilters, surveyIdParam]);

  useEffect(() => {
    if (lastActionRef.current !== 'reset') {
      dispatch({
        type: 'syncFilters',
        payload: filtersToSearchClauses(localFilters, globalFilters),
      });
    }
    lastActionRef.current = 'syncFilters';
  }, [localFilters, globalFilters]);

  const setPageSize = useCallback((newPageSize: number) => {
    dispatch({ type: 'setPageSize', payload: newPageSize });
  }, []);
  const setPageNumber = useCallback((newPageNumber: number) => {
    dispatch({ type: 'setPageNumber', payload: newPageNumber });
  }, []);

  return { ...responsesViewState, setPageSize, setPageNumber };
};
